import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ---- Hardcoded demo identities (must match the arrays used elsewhere) ----
// IDs now use last four only
export const CUSTOMER_IDS = [
  "6504",
  "6152",
  "3540",
  "8563",
  "8472",
  "6501",
  "1025",
  "3765",
  "6509",
  "1837",
];

export const CUSTOMER_NAMES = [
  "Marshall Eriksen",
  "Ted Mosby",
  "Barney Stinson",
  "Lily Aldrin",
  "Ranjit Singh",
  "Quinn Garvey",
  "Zoey Pierson",
  "Jerry Whitaker",
  "Sandy Rivers",
  "Daniel Flores",
];

// Identical behavior to your existing helper
const getStableCustomerIndex = () => {
  if (typeof window !== "undefined") {
    if (window.__gtsDemoCustomerIndex != null) return window.__gtsDemoCustomerIndex;
    const idx = Math.floor(Math.random() * CUSTOMER_IDS.length);
    window.__gtsDemoCustomerIndex = idx;
    return idx;
  }
  return Math.floor(Math.random() * CUSTOMER_IDS.length);
};

// Fire when the selected user changes so other components can react (optional)
const announceCustomerChange = () => {
  try {
    window.dispatchEvent(new CustomEvent("gts:customerChanged"));
  } catch (_) {}
};

// A tiny store of the active user index, kept the same way as your code
const setActiveCustomerIndex = (idx) => {
  window.__gtsDemoCustomerIndex = idx;
  announceCustomerChange();
};

const getActiveCustomerIndex = () => {
  // mirrors how the header/Main reads the index
  return window.__gtsDemoCustomerIndex ?? getStableCustomerIndex();
};

// ---------------------------------------------------------------------------
// LoginSelector (no mode selector; always shows the login form)
// - Accepts any password if the ID is valid
// - Stores user via window.__gtsDemoCustomerIndex
// - Sets manual-login flags in localStorage
// - Redirects to home (/) ~1s after successful auth
// ---------------------------------------------------------------------------
export default function LoginSelector() {
  const [idInput, setIdInput] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  // keep reference (unused visually here, but harmless)
  useMemo(() => getActiveCustomerIndex(), []);

  useEffect(() => {
    setError("");
    setSuccess("");
  }, [idInput, pwInput]);

  useEffect(() => {
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, []);

  const validateId = (id) => {
    if (!id) return { ok: false, idx: -1 };
    const trimmed = id.trim();
    const idx = CUSTOMER_IDS.indexOf(trimmed);
    return { ok: idx !== -1, idx };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      const { ok, idx } = validateId(idInput);
      if (!ok) {
        setError("Invalid ID. Use one of the provided IDs.");
        return;
      }

      // Accept any password. Persist selection.
      setActiveCustomerIndex(idx);

      // Ensure header switches to manual mode
      try {
        window.localStorage.setItem("gts.manualLoginEnabled", "true");
        window.localStorage.setItem("gts.manualLoginIndex", String(idx));
      } catch {}

      setSuccess(`Signed in as ${CUSTOMER_NAMES[idx]} (ID ${CUSTOMER_IDS[idx]})`);

      requestAnimationFrame(() => {
        document.getElementById("login-success-msg")?.focus?.();
      });

      // Redirect to home after ~1s
      redirectTimer.current = setTimeout(() => navigate("/"), 1000);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { display: "block", fontWeight: 600, marginBottom: 6 };
  const inputStyle = {
    display: "block",
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #d0d5dd",
    borderRadius: 10,
    lineHeight: 1.4,
  };

  return (
    <section className="login-selector">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-8">
            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="row row-gutter-16">
                <div className="col-12 col-lg-6" style={{ marginBottom: 16 }}>
                  <label htmlFor="username" className="form-label" style={labelStyle}>
                    ID
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    className="input-text"
                    placeholder="Enter ID (exact match)"
                    value={idInput}
                    onChange={(e) => setIdInput(e.target.value)}
                    disabled={submitting}
                    aria-required="true"
                    autoComplete="username"
                    style={inputStyle}
                  />
                </div>

                <div className="col-12 col-lg-6" style={{ marginBottom: 16 }}>
                  <label htmlFor="password" className="form-label" style={labelStyle}>
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    className="input-text"
                    placeholder="Password"
                    value={pwInput}
                    onChange={(e) => setPwInput(e.target.value)}
                    disabled={submitting}
                    aria-required="true"
                    autoComplete="current-password"
                    style={inputStyle}
                  />
                </div>

                <div className="col-12">
                  {error && (
                    <p className="error-message" style={{ color: "red", marginTop: 4 }}>{error}</p>
                  )}
                  {success && (
                    <p
                      id="login-success-msg"
                      tabIndex={-1}
                      className="success-message"
                      style={{ color: "green", marginTop: 4 }}
                    >
                      {success}
                    </p>
                  )}
                </div>

                <div className="col-12" style={{ marginTop: 12 }}>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Signing in…" : "Sign In"}
                  </button>
                </div>
              </div>

              {/* Quick-pick (click to autofill ID) */}
              <div style={{ marginTop: 20 }}>
                <details>
                  <summary>Select an ID</summary>
                  <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
                    {CUSTOMER_IDS.map((id, i) => (
                      <li key={id} style={{ marginBottom: 6 }}>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => setIdInput(id)}
                          disabled={submitting}
                        >
                          {CUSTOMER_NAMES[i]} — {id}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
